import { useCallback, type ReactNode } from 'react';
import { audioEngine } from '../../../audio/audioEngine';
import { readDomMood } from '../hooks/readDomMood';
import { getActiveMinigameSeUrl } from '../hooks/minigameSe';
import { Button, Icon } from '../primitives';
import './semantic.groupA.css';

/**
 * Foundation semantic: AdvanceTrigger（Phase D §4.4 / Phase B 凍結対象 API）
 *
 * Visual Grammar:
 *  - 「次へ」進行は右or下方向の含意。既定アイコンは lucide ChevronRight。
 *  - variant で色を切り替えるが、形状（角丸・サイズ階段）は Button primitive と統一。
 *
 * 配色トークン:
 *  - default: --mg-accent（Button primary をそのまま）
 *  - success: --mg-success（semantic.groupA.css の .mg-advance--success で上書き）
 *  - failure: --mg-error（semantic.groupA.css の .mg-advance--failure で上書き）
 *
 * 規約:
 *  - hex 直書き禁止。色は mood トークン経由（semantic.groupA.css 側）。
 *  - アイコンは Icon primitive を使う（直接 lucide-react を呼ばない）。
 */
export type AdvanceTriggerVariant = 'default' | 'success' | 'failure';
export type AdvanceTriggerSize = 'sm' | 'md' | 'lg';

export interface AdvanceTriggerProps {
  /** 表示ラベル。未指定時は「次へ進む」 */
  label?: string;
  /** ボタン押下時のコールバック（Visual Grammar: 進行） */
  onAdvance: () => void;
  /** 操作不可状態。Button primitive の disabled へ伝播する */
  disabled?: boolean;
  /** 色トーン。default=accent / success=success / failure=error */
  variant?: AdvanceTriggerVariant;
  /** サイズ。Button primitive の size と同義 */
  size?: AdvanceTriggerSize;
  /** アイコン差し替え（既定: ChevronRight）。null 相当を渡したい場合は <></> を入れる */
  icon?: ReactNode;
}

export function AdvanceTrigger({
  label = '次へ進む',
  onAdvance,
  disabled = false,
  variant = 'default',
  size = 'md',
  icon,
}: AdvanceTriggerProps) {
  // R2 Wave2B: 「進行/確定」の瞬間に select 音（mood は data-mood 追従）。
  // AdvanceTrigger は「進行=確定」の唯一の操作部品で、StartGate の開始ボタン・
  // JudgementModal の「次へ進む」も内部はこの AdvanceTrigger。よって select 音を
  // ここ 1 箇所に集約すれば、それら全てに開始/進行音が自動で届く（StartGate 等で
  // 二重に鳴らさないこと）。成功モーダルでは success(mount) → 後で click 時 select の順で、
  // 別イベントなので二重発火にはならない。
  const handleAdvance = useCallback(() => {
    // P8-1: config.audio.tapSE（アセット SE）が指定されていればそれを優先する。
    // 未指定・未登録（Sandbox 等）は従来どおり合成 select 音（挙動不変）。
    const tapSeUrl = getActiveMinigameSeUrl('tap');
    if (tapSeUrl) {
      audioEngine.playSfxAsset(tapSeUrl);
    } else {
      const mood = readDomMood();
      audioEngine.playSfx('select', mood ? { mood } : undefined);
    }
    onAdvance();
  }, [onAdvance]);

  // 既定アイコンは ChevronRight（Visual Grammar 固定）。icon prop で差し替え可能。
  const renderedIcon = icon !== undefined ? icon : <Icon name="ChevronRight" />;

  // variant 別の追加クラス。default は Button primary をそのまま使うため無印で十分。
  const variantClass =
    variant === 'success'
      ? 'mg-advance mg-advance--success'
      : variant === 'failure'
        ? 'mg-advance mg-advance--failure'
        : 'mg-advance';

  return (
    <Button
      variant="primary"
      size={size}
      disabled={disabled}
      onClick={handleAdvance}
      className={variantClass}
      // aria-label は label を継承させ、文字＋アイコンの組合せで読み上げが冗長にならないようにする
      aria-label={label}
    >
      <span>{label}</span>
      {renderedIcon}
    </Button>
  );
}
